import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, Eye, EyeOff, KeyRound } from 'lucide-react';
import api from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import RecoveryCodeModal from '@/components/RecoveryCodeModal';
import LegalFooter from '@/components/LegalFooter';

export default function ForgotPassword() {
    const { setUser } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [newCode, setNewCode] = useState(null);
    const [emailBusy, setEmailBusy] = useState(false);
    const [emailMsg, setEmailMsg] = useState(null);

    const sendEmailLink = async () => {
        if (!email) return;
        setEmailBusy(true); setEmailMsg(null);
        try {
            await api.post('/auth/reset/email-magic-link', { email: email.trim() });
            setEmailMsg({ ok: true, text: 'If that email is on file, a reset link is on its way (good for 15 minutes).' });
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setEmailMsg({ ok: false, text: typeof detail === 'string' ? detail : 'Could not send email right now. Try again later.' });
        } finally { setEmailBusy(false); }
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setSubmitting(true);
        try {
            const { data } = await api.post('/auth/reset/recovery', {
                email: email.trim(),
                recovery_code: code,
                new_password: password,
            });
            // Token + user returned — log them in immediately and surface the
            // new recovery code (the old one is now invalidated).
            localStorage.setItem('qm_token', data.token);
            setUser(data.user);
            setNewCode(data.recovery_code);
        } catch (err) {
            const detail = err?.response?.data?.detail;
            setError(typeof detail === 'string' ? detail : 'Could not reset password. Please try again.');
        } finally {
            setSubmitting(false);
        }
    };

    const onContinue = () => {
        setNewCode(null);
        navigate('/app/home', { replace: true });
    };

    return (
        <div className="min-h-screen bg-background relative overflow-hidden flex flex-col">
            <div className="pointer-events-none absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-sage/40 blur-3xl" />
            <div className="pointer-events-none absolute -bottom-40 -right-40 w-[460px] h-[460px] rounded-full bg-dusty/50 blur-3xl" />

            <div className="grid md:grid-cols-2 flex-1">

            <div className="relative z-10 flex items-center justify-center px-6 py-12">
                <div className="w-full max-w-md" data-testid="forgot-password-page">
                    <Link to="/auth" className="inline-flex items-center gap-2 text-forest-soft hover:text-forest text-sm mb-8" data-testid="forgot-back-link">
                        <ArrowLeft size={16} /> Back to sign in
                    </Link>
                    <div className="w-12 h-12 rounded-2xl bg-sage/40 grid place-items-center mb-3">
                        <KeyRound size={22} className="text-forest" />
                    </div>
                    <h1 className="font-serif text-4xl text-forest">Reset your password</h1>
                    <p className="text-forest-soft mt-2">
                        Enter your email and the recovery code you saved at signup. We’ll set a new password and give you a fresh code.
                    </p>

                    <form onSubmit={onSubmit} className="mt-8 space-y-4" data-testid="forgot-form">
                        <div>
                            <label className="text-sm text-forest-soft" htmlFor="fp-email">Email</label>
                            <input
                                id="fp-email"
                                type="email" required
                                value={email} onChange={(e) => setEmail(e.target.value)}
                                placeholder="you@example.com"
                                className="qm-input mt-1"
                                autoComplete="email"
                                data-testid="forgot-email-input"
                            />
                        </div>
                        <div>
                            <label className="text-sm text-forest-soft" htmlFor="fp-code">Recovery code</label>
                            <input
                                id="fp-code"
                                type="text" required
                                value={code}
                                onChange={(e) => setCode(e.target.value.toUpperCase())}
                                placeholder="QMR-XXXX-XXXX-XXXX-XXXX"
                                className="qm-input mt-1 font-mono tracking-widest"
                                data-testid="forgot-code-input"
                                autoCapitalize="characters"
                                spellCheck={false}
                            />
                        </div>
                        <div>
                            <label className="text-sm text-forest-soft" htmlFor="fp-new">New password</label>
                            <div className="relative mt-1">
                                <input
                                    id="fp-new"
                                    type={showPassword ? 'text' : 'password'} required minLength={6}
                                    value={password} onChange={(e) => setPassword(e.target.value)}
                                    placeholder="At least 6 characters"
                                    className="qm-input pr-12 w-full"
                                    autoComplete="new-password"
                                    data-testid="forgot-new-password-input"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword((s) => !s)}
                                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                                    data-testid="forgot-password-toggle"
                                    className="absolute inset-y-0 right-2 my-auto h-9 w-9 grid place-items-center rounded-full text-forest-soft hover:bg-sage/30 hover:text-forest"
                                >
                                    {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-2xl px-4 py-3" data-testid="forgot-error">
                                {error}
                            </div>
                        )}

                        <button
                            type="submit"
                            disabled={submitting}
                            data-testid="forgot-submit-button"
                            className="qm-btn-primary w-full disabled:opacity-60"
                        >
                            {submitting ? 'A moment …' : 'Reset password'}
                        </button>
                    </form>

                    <div className="mt-6 border-t border-border/60 pt-5" data-testid="forgot-email-fallback">
                        <p className="text-sm text-forest-soft mb-2">Lost your recovery code?</p>
                        <button
                            type="button"
                            onClick={sendEmailLink}
                            disabled={!email || emailBusy}
                            data-testid="forgot-email-link-button"
                            className="qm-btn-ghost text-sm disabled:opacity-60"
                        >
                            {emailBusy ? 'Sending…' : 'Email me a reset link instead'}
                        </button>
                        {emailMsg && (
                            <p
                                data-testid={emailMsg.ok ? 'forgot-email-ok' : 'forgot-email-err'}
                                className={`text-sm mt-2 ${emailMsg.ok ? 'text-forest' : 'text-red-700'}`}
                            >
                                {emailMsg.text}
                            </p>
                        )}
                    </div>

                    <p className="text-xs text-forest-soft mt-6">
                        Lost your recovery code too? Sign in with your old password if you still remember it, and you can regenerate a fresh code from your Profile.
                    </p>
                </div>
            </div>

            <div className="relative hidden md:block">
                <div
                    className="absolute inset-0"
                    style={{
                        backgroundImage:
                            'linear-gradient(160deg, rgba(168,197,176,0.55), rgba(232,208,208,0.45) 60%, rgba(200,182,219,0.45)), url(https://images.unsplash.com/photo-1518837695005-2083093ee35b?auto=format&fit=crop&w=1200&q=80)',
                        backgroundSize: 'cover',
                        backgroundPosition: 'center',
                    }}
                />
                <div className="relative h-full grid place-items-center p-10">
                    <blockquote className="max-w-md text-cream font-serif text-2xl leading-relaxed text-center drop-shadow">
                        “Begin again. As many times as it takes.”
                    </blockquote>
                </div>
            </div>
            </div>

            <LegalFooter />

            {newCode && (
                <RecoveryCodeModal
                    code={newCode}
                    email={email}
                    onClose={onContinue}
                    title="Password reset — save your new code"
                    intro="Your password is updated. Here’s your new recovery code — please save it. The old code no longer works."
                />
            )}
        </div>
    );
}
